// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Mail > Hidden from Chats. The rules that keep mail out of
// the chat lists (it stays in Mail), each with a remove button, and a row to
// add one by sender address or subject phrase. Rules are usually made from a
// mail row in Chats ("Hide from Chats"); this is where they are looked after.

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {ruleLabel} from '@app/features/konvo_mail/components/MailChatHideMenu';
import styles from '@app/features/konvo_mail/components/MailChatHideSettings.module.css';
import MailChatHide from '@app/features/konvo_mail/state/MailChatHide';
import {
	MAIL_CHAT_HIDE_MAX_RULES,
	MAIL_CHAT_HIDE_MAX_VALUE,
	type MailChatHideKind,
	normalizeRule,
} from '@app/features/konvo_mail/utils/MailChatHiding';
import {AtIcon, PlusIcon, QuotesIcon, XIcon} from '@app/features/ui/icons/Icons';
import {ActionButton, IconButton, Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const REMOVE_RULE_DESCRIPTOR = msg({
	message: 'Remove the rule “{rule}”',
	comment:
		'Accessible label of the remove button on a rule in Settings > Mail > Hidden from Chats. {rule} reads like "From notifications@github.com".',
});

export const MailChatHideSettings = observer(function MailChatHideSettings() {
	const {t, i18n} = useLingui();
	const [kind, setKind] = useState<MailChatHideKind>('sender');
	const [value, setValue] = useState('');
	useEffect(() => {
		void KonvoCalendarPrefs.load();
	}, []);
	const rules = MailChatHide.rules;
	const candidate = normalizeRule({kind, value});
	const duplicate = candidate != null && MailChatHide.has(candidate);
	const full = rules.length >= MAIL_CHAT_HIDE_MAX_RULES;
	const canAdd = candidate != null && !duplicate && !full;
	const add = () => {
		if (!candidate || !MailChatHide.add(candidate)) return;
		setValue('');
	};
	const kindOptions: ReadonlyArray<{value: MailChatHideKind; children: string; 'data-flx': string}> = [
		{value: 'sender', children: t`From`, 'data-flx': 'user.konvo-mail-settings-tab.chat-hide.kind-sender'},
		{
			value: 'subject',
			children: t`Subject contains`,
			'data-flx': 'user.konvo-mail-settings-tab.chat-hide.kind-subject',
		},
	];
	return (
		<SettingsSection
			id="konvo-mail-hidden-from-chats"
			title={<Trans>Hidden from Chats</Trans>}
			description={<Trans>These mails stay in Mail but don't show in your chats.</Trans>}
		>
			<div className={kitStyles.rows} data-flx="user.konvo-mail-settings-tab.chat-hide.rules">
				{rules.length === 0 ? (
					<p className={styles.empty} data-flx="user.konvo-mail-settings-tab.chat-hide.empty">
						<Trans>
							Nothing is hidden. Right-click or long-press a mail in Chats and choose Hide from Chats, or add a rule
							below.
						</Trans>
					</p>
				) : (
					rules.map((rule) => {
						const label = ruleLabel(i18n, rule);
						return (
							<SettingsRow
								key={`${rule.kind}:${rule.value}`}
								icon={rule.kind === 'sender' ? <AtIcon size={20} /> : <QuotesIcon size={20} />}
								title={<span className={styles.ruleText}>{label}</span>}
								control={() => (
									<IconButton
										label={i18n._(REMOVE_RULE_DESCRIPTOR, {rule: label})}
										icon={<XIcon size={18} />}
										onClick={() => MailChatHide.remove(rule)}
										data-flx="user.konvo-mail-settings-tab.chat-hide.remove"
									/>
								)}
								data-flx="user.konvo-mail-settings-tab.chat-hide.rule"
							/>
						);
					})
				)}
			</div>
			<form
				className={styles.add}
				onSubmit={(event) => {
					event.preventDefault();
					add();
				}}
				data-flx="user.konvo-mail-settings-tab.chat-hide.add"
			>
				<Segmented
					compact
					value={kind}
					options={kindOptions}
					onChange={setKind}
					aria-label={t`Hide mail by`}
					data-flx="user.konvo-mail-settings-tab.chat-hide.kind"
				/>
				<input
					className={styles.input}
					type={kind === 'sender' ? 'email' : 'text'}
					inputMode={kind === 'sender' ? 'email' : 'text'}
					autoCapitalize="off"
					autoCorrect="off"
					spellCheck={false}
					value={value}
					maxLength={MAIL_CHAT_HIDE_MAX_VALUE}
					placeholder={kind === 'sender' ? t`notifications@example.com` : t`Words in the subject`}
					aria-label={kind === 'sender' ? t`Sender's address` : t`Words in the subject`}
					onChange={(event) => setValue(event.target.value)}
					data-flx="user.konvo-mail-settings-tab.chat-hide.value"
				/>
				<ActionButton
					type="submit"
					small
					tone="secondary"
					icon={<PlusIcon size={16} />}
					disabled={!canAdd}
					data-flx="user.konvo-mail-settings-tab.chat-hide.add-button"
				>
					<Trans>Add</Trans>
				</ActionButton>
			</form>
			{duplicate ? (
				<p className={kitStyles.note}>
					<Trans>That rule is already on the list.</Trans>
				</p>
			) : full ? (
				<p className={kitStyles.note}>
					<Trans>You can keep up to 200 rules. Remove one first.</Trans>
				</p>
			) : null}
		</SettingsSection>
	);
});
